/**
 * The one visual contract every result card is built from.
 *
 * Nothing here is invented. The tray, the plate, the hairline and the field
 * grid are the paint the session product panel already puts around session
 * content, so a result card dropped into a deliverable reads as the same object
 * family as the map, gantt and chart widgets already living there. The text
 * cast is the one the two live-verified exemplars use — a deliberate cool
 * white against the mint accent rather than a neutral one.
 *
 * The radius ladder is the part with teeth: it is the only derivation that
 * reproduces the measured good state on both exemplars (12 / 9 / 7 for
 * shell / core / media). `container − inset` would put hotel media at 2 and
 * `container − 4` at 5, and both contradict a component that has been measured
 * and signed off.
 */
import type { CSSProperties } from "react";
import { getFontFamily } from "@/theme/typography";
import {
  CresultcardFigureScale,
  CresultcardLaneSlot,
  CresultcardMarkSize,
  CresultcardRowIdentityFloor,
  CresultcardStackDensity,
  CresultcardSublineSize,
  CresultcardTextStep,
  CresultcardTilePadding,
  CresultcardTitleLines,
  CresultcardTone,
} from "./cresultcard.types";

/** The system accent. Pillars differentiate by glyph, never by hue. */
export const CRC_ACCENT = "#66FEC1";

/* ── The radius ladder ────────────────────────────────────────────────────── */

/**
 * One scale, walked one rung per nesting level. The deltas decay (4, 4, 3, 2,
 * 2, 1), which is what lands nested media 2–4px tighter than its holder at
 * every point without anyone doing arithmetic at a call site.
 */
export const CRC_RADIUS_LADDER: readonly number[] = [20, 16, 12, 9, 7, 5, 4, 0];

export const CRC_R_TRAY = 20;
export const CRC_R_PLATE = 16;
export const CRC_R_CARD = 12;
export const CRC_R_CORE = 9;
export const CRC_R_TILE = 7;
export const CRC_R_INSET = 5;
export const CRC_R_THUMB = 4;
export const CRC_R_FLUSH = 0;
/** Off the ladder, and it does not reset the context: chips and capsules only. */
export const CRC_R_PILL = 999;

/**
 * The next rung down. A rung that is not on the ladder (a pill, a caller's own
 * number) resolves to the nearest rung below it, so the walk never stalls.
 */
export function radiusBelow(radius: number): number {
  const next = CRC_RADIUS_LADDER.find((rung) => rung < radius);
  return next ?? CRC_R_FLUSH;
}

/* ── Geometry ─────────────────────────────────────────────────────────────── */

/** The tray's own frame width. Below this the rim stops reading as a rim. */
export const CRC_TRAY_PAD = 4;
/** Rail cards are narrow so a second card always peeks: the rail must LOOK scrollable. */
export const CRC_RAIL_CARD_WIDTH = 236;
/** The rail's gap and its own inset, shared so a card's snap point is predictable. */
export const CRC_RAIL_GAP = 9;
export const CRC_RAIL_INSET = 13;
/**
 * The measured version-content column: a 607.44px content tile with 25.6px of
 * padding each side. A widget drawn wider than the place it lives is not a
 * design of that widget.
 */
export const CRC_VERSION_CONTENT_WIDTH = 556;
/** The floor for any interactive target at Mobile. The kit sizes controls; a pillar does not. */
export const CRC_TOUCH_TARGET = 44;

/**
 * What a row's identity zone may declare as its minimum.
 *
 * A 548px row spends 26px on its own inline padding and 20px on its two gaps,
 * so 502px is what the three zones share. `Title` holds a little under a third
 * of that and `Wide` a little under half — both leave a trailing zone that can
 * still carry a figure, which a larger floor would not. `None` is 0, which is
 * the `minmax(0, 1fr)` every row is measured with today.
 */
export const CRC_ROW_IDENTITY_FLOOR: Record<
  CresultcardRowIdentityFloor,
  number
> = {
  [CresultcardRowIdentityFloor.None]: 0,
  [CresultcardRowIdentityFloor.Title]: 160,
  [CresultcardRowIdentityFloor.Wide]: 220,
};

/* ── Colour ───────────────────────────────────────────────────────────────── */

export const CRC_TONE_COLOR: Record<CresultcardTone, string> = {
  [CresultcardTone.Positive]: CRC_ACCENT,
  [CresultcardTone.Negative]: "#FF6E84",
  [CresultcardTone.Caution]: "#F29F57",
  [CresultcardTone.Neutral]: "rgba(244,253,250,0.94)",
  [CresultcardTone.Muted]: "rgba(221,244,237,0.5)",
};

const HEX_TONE_PATTERN = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const RGB_TONE_PATTERN = /^rgba?\(([^)]*)\)$/i;
const RGB_COMPONENT_SEPARATOR = ",";
const HEX_RADIX = 16;
const OPAQUE_ALPHA = 1;
const ALPHA_PRECISION = 4;

interface CrcToneAlphaInput {
  readonly tone: CresultcardTone;
  readonly alpha: number;
}

/**
 * A tone colour at a stated alpha.
 *
 * Three of the five tones are hex and two are `rgba()` strings, so appending a
 * two-digit hex alpha — which is what every tinted surface used to do — produced
 * `rgba(244,253,250,0.94)12` for `Neutral` and `Muted`. That is not a colour:
 * the browser drops the declaration, and a five-tone component silently worked
 * for three tones. Alpha is multiplied rather than replaced, so a tone that is
 * already quiet stays quieter than one that is not.
 */
export function crcToneAlpha({ tone, alpha }: CrcToneAlphaInput): string {
  const color = CRC_TONE_COLOR[tone];
  const hex = HEX_TONE_PATTERN.exec(color);
  if (hex != null) {
    const [, red, green, blue] = hex;
    return `rgba(${String(parseInt(red, HEX_RADIX))}, ${String(parseInt(green, HEX_RADIX))}, ${String(parseInt(blue, HEX_RADIX))}, ${alpha.toFixed(ALPHA_PRECISION)})`;
  }
  const rgb = RGB_TONE_PATTERN.exec(color);
  if (rgb == null) {
    return color;
  }
  const parts = rgb[1]
    .split(RGB_COMPONENT_SEPARATOR)
    .map((part) => part.trim());
  const [red, green, blue, existingAlpha] = parts;
  const combined =
    (existingAlpha == null ? OPAQUE_ALPHA : Number(existingAlpha)) * alpha;
  return `rgba(${red}, ${green}, ${blue}, ${combined.toFixed(ALPHA_PRECISION)})`;
}

export const CRC_TEXT = "rgba(244,253,250,0.94)";
/**
 * The ink a filled mint surface is legible against. It is the plate's own core
 * rather than black: pure black on mint reads as a sticker, and every other
 * dark in the system is this one.
 */
export const CRC_ON_ACCENT = "#04150E";
export const CRC_TEXT_MUTED = "rgba(221,244,237,0.5)";
export const CRC_TEXT_FAINT = "rgba(221,244,237,0.34)";
export const CRC_MONEY_SUFFIX_INK = CRC_TEXT_MUTED;
export const CRC_HAIRLINE = "rgba(255,255,255,0.08)";
export const CRC_DIVIDER = "rgba(255,255,255,0.06)";

/**
 * How far along the accent ramp a lane rule sits. One hue, five weights.
 *
 * The first four are the measured ones and do not move: a fifth position that
 * re-spaced the ramp would recolour every lane already on it. The fifth
 * continues the decay of the existing deltas (0.28, 0.20, 0.16 → 0.10) rather
 * than being chosen, which is what keeps it distinguishable from the fourth
 * while staying above the plate's own hairline.
 */
export const CRC_LANE_OPACITY: Record<CresultcardLaneSlot, number> = {
  [CresultcardLaneSlot.Primary]: 0.9,
  [CresultcardLaneSlot.Secondary]: 0.62,
  [CresultcardLaneSlot.Tertiary]: 0.42,
  [CresultcardLaneSlot.Quaternary]: 0.26,
  [CresultcardLaneSlot.Quinary]: 0.16,
};

/* ── Surfaces ─────────────────────────────────────────────────────────────── */

export const CRC_TRAY_BACKGROUND_IMAGE = [
  "linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.035) 36%, rgba(102,254,193,0.18))",
  "radial-gradient(circle at 18% 0%, rgba(102,254,193,0.24), transparent 34%)",
  "radial-gradient(circle at 98% 110%, rgba(91,127,255,0.16), transparent 38%)",
].join(", ");

export const CRC_TRAY_SHADOW = [
  "inset 0 1px 0 rgba(255,255,255,0.16)",
  "inset 0 -1px 0 rgba(102,254,193,0.11)",
  "0 24px 80px -52px rgba(102,254,193,0.26)",
  "0 16px 48px -36px rgba(0,0,0,0.24)",
].join(", ");

export const CRC_PLATE_BACKGROUND_COLOR = "rgba(15,19,29,0.58)";
export const CRC_PLATE_BACKGROUND_IMAGE = [
  "radial-gradient(500px 250px at 100% 0%, rgba(102,254,193,0.05), transparent 60%)",
  "linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.55))",
].join(", ");
export const CRC_PLATE_SHADOW = [
  "inset 0 0 0 1px rgba(255,255,255,0.08)",
  "0 20px 50px -20px rgba(0,0,0,0.6)",
].join(", ");

export const CRC_CARD_BACKGROUND_COLOR = "rgba(220,255,245,0.09)";
export const CRC_CARD_BACKGROUND_IMAGE =
  "radial-gradient(circle at 9% 0%, rgba(102,254,193,0.17), transparent 28%)";
export const CRC_CARD_SHADOW = [
  "inset 0 1px 0 rgba(255,255,255,0.16)",
  "inset 0 -1px 0 rgba(102,254,193,0.06)",
].join(", ");

export const CRC_CORE_BACKGROUND_COLOR = "#041012";
export const CRC_CORE_BACKGROUND_IMAGE =
  "radial-gradient(circle at 88% -8%, rgba(76,163,255,0.08), transparent 31%)";
export const CRC_CORE_SHADOW = [
  "inset 0 1px 0 rgba(255,255,255,0.075)",
  "inset 0 0 0 1px rgba(255,255,255,0.035)",
].join(", ");

/** Raised block on a plate. */
export const CRC_TILE_BACKGROUND_COLOR = "rgba(224,255,247,0.045)";
export const CRC_TILE_SHADOW = [
  "inset 0 1px 0 rgba(255,255,255,0.055)",
  "inset 0 0 0 1px rgba(255,255,255,0.03)",
].join(", ");

/** Sunk block: a ledger body, an offer row, an inset well. */
export const CRC_WELL_BACKGROUND_COLOR = "rgba(0,0,0,0.22)";
export const CRC_WELL_SHADOW = "inset 0 0 0 1px rgba(255,255,255,0.05)";

/** The plate a media frame reserves before its asset resolves. */
export const CRC_MEDIA_PLATE_BACKGROUND_IMAGE =
  "linear-gradient(150deg, rgba(102,254,193,0.06), rgba(0,0,0,0.5) 62%)";
export const CRC_MEDIA_PLATE_SHADOW = "inset 0 0 0 1px rgba(255,255,255,0.07)";

/** Bottom-anchored scrim so overlaid type stays readable on any photograph. */
export const CRC_MEDIA_SCRIM_IMAGE =
  "linear-gradient(180deg, rgba(0,0,0,0) 38%, rgba(0,0,0,0.62) 88%, rgba(0,0,0,0.78) 100%)";

/**
 * The faint field grid every plate carries behind its content, masked to the
 * upper body so it never collides with the bounds line's small type.
 */
export const CRC_FIELD_GRID_STYLE: CSSProperties = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  backgroundImage: [
    "linear-gradient(rgba(255,255,255,0.028) 1px, transparent 1px)",
    "linear-gradient(90deg, rgba(255,255,255,0.028) 1px, transparent 1px)",
  ].join(", "),
  backgroundSize: "28px 28px",
  maskImage:
    "radial-gradient(ellipse 70% 62% at 50% 34%, rgba(0,0,0,0.75) 0%, transparent 100%)",
  WebkitMaskImage:
    "radial-gradient(ellipse 70% 62% at 50% 34%, rgba(0,0,0,0.75) 0%, transparent 100%)",
};

/* ── Type ─────────────────────────────────────────────────────────────────── */

/**
 * Declared once, on the tray, and INHERITED by everything inside it. No type
 * step carries a family: a font repeated on two hundred elements is two
 * hundred places for the design system's font to stop being the font.
 */
export const CRC_FONT_FAMILY = getFontFamily();

/** Every figure in every card is tabular: columns of numbers must not shimmy. */
export const CRC_NUMERIC_STYLE: CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1',
};

export const CRC_TYPE_STYLE: Record<CresultcardTextStep, CSSProperties> = {
  [CresultcardTextStep.Focal]: {
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: "-0.03em",
    lineHeight: 1,
    color: CRC_TEXT,
  },
  [CresultcardTextStep.Lead]: {
    fontSize: 17,
    fontWeight: 780,
    letterSpacing: "-0.022em",
    lineHeight: 1.1,
    color: CRC_TEXT,
  },
  [CresultcardTextStep.Title]: {
    fontSize: 15,
    fontWeight: 730,
    letterSpacing: "-0.018em",
    lineHeight: 1.22,
    color: CRC_TEXT,
  },
  [CresultcardTextStep.Body]: {
    fontSize: 12,
    fontWeight: 450,
    lineHeight: 1.5,
    color: "rgba(221,244,237,0.62)",
  },
  [CresultcardTextStep.Meta]: {
    fontSize: 10.5,
    fontWeight: 540,
    lineHeight: 1.4,
    color: CRC_TEXT_MUTED,
  },
  [CresultcardTextStep.Eyebrow]: {
    fontSize: 9,
    fontWeight: 740,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    lineHeight: 1,
    color: "rgba(221,244,237,0.62)",
  },
};

/** The figure ramp, in one place, so no composition passes a pixel size. */
export const CRC_FIGURE_SIZE: Record<CresultcardFigureScale, number> = {
  [CresultcardFigureScale.Row]: 13,
  [CresultcardFigureScale.Rail]: 17,
  [CresultcardFigureScale.Card]: 21,
  [CresultcardFigureScale.Detail]: 27,
  [CresultcardFigureScale.Focal]: 34,
};

export const CRC_SUBLINE_SIZE: Record<CresultcardSublineSize, number> = {
  [CresultcardSublineSize.Small]: 9.5,
  [CresultcardSublineSize.Regular]: 11,
};

/**
 * `Full` carries no clamp at all. It is a member rather than `undefined` so a
 * caller has to say which one they mean.
 */
export const CRC_TITLE_CLAMP: Record<
  CresultcardTitleLines,
  number | undefined
> = {
  [CresultcardTitleLines.One]: 1,
  [CresultcardTitleLines.Two]: 2,
  [CresultcardTitleLines.Three]: 3,
  [CresultcardTitleLines.Full]: undefined,
};

/* ── Rhythm ───────────────────────────────────────────────────────────────── */

export const CRC_STACK_GAP: Record<CresultcardStackDensity, number> = {
  [CresultcardStackDensity.Tight]: 4,
  [CresultcardStackDensity.Snug]: 7,
  [CresultcardStackDensity.Regular]: 10,
  [CresultcardStackDensity.Loose]: 14,
};

export const CRC_TILE_INSET: Record<CresultcardTilePadding, number> = {
  [CresultcardTilePadding.None]: 0,
  [CresultcardTilePadding.Snug]: 8,
  [CresultcardTilePadding.Regular]: 11,
};

export const CRC_MARK_PIXELS: Record<CresultcardMarkSize, number> = {
  [CresultcardMarkSize.Row]: 22,
  [CresultcardMarkSize.Card]: 28,
  [CresultcardMarkSize.Lead]: 32,
  [CresultcardMarkSize.Band]: 38,
  [CresultcardMarkSize.Hero]: 56,
};

/* ── Clamping ─────────────────────────────────────────────────────────────── */

/** `-webkit-box` is still the only cross-browser clamp; its two magic values live here. */
enum CrcBoxOrient {
  Vertical = "vertical",
}

enum CrcBoxDisplay {
  WebkitBox = "-webkit-box",
}

/**
 * How a clamp CUTS, which is a separate decision from how many lines it allows.
 *
 * `-webkit-line-clamp` supplies its own ellipsis, but only for the case it
 * measures: more lines than allowed. A single word WIDER than the box is not
 * that case — it overflows on the inline axis, and `overflow: hidden` alone cuts
 * it mid-glyph with nothing to say so. That is how a two-line clamp rendered
 * `Gold` as `Gol`, and it was latent in every clamped identity in the system: a
 * one-line title truncates with an ellipsis (`CRC_TRUNCATE_STYLE`) while a
 * two-line one hard-cut, so the two disagreed about the same event.
 *
 * `break-word` first, so the allowance is actually spent — a long word uses the
 * second line instead of hanging off the first. `ellipsis` for what is still too
 * long after that. `break-word` rather than `anywhere` on purpose: `anywhere`
 * lets min-content collapse to one character, which is what turns a squeezed
 * identity zone into a column of single letters instead of pushing back.
 */
const CRC_CLAMP_CUT: CSSProperties = {
  textOverflow: "ellipsis",
  overflowWrap: "break-word",
};

export function crcClampLines(lines: number): CSSProperties {
  return {
    ...CRC_CLAMP_CUT,
    display: CrcBoxDisplay.WebkitBox,
    WebkitBoxOrient: CrcBoxOrient.Vertical,
    WebkitLineClamp: lines,
    overflow: "hidden",
  };
}

/**
 * Present to assistive technology, absent from the layout. A lane's own name
 * and a set's live region are both text a reader does not need to SEE and a
 * screen reader must not miss, and `display: none` would take them out of the
 * accessibility tree along with the layout.
 */
export const CRC_VISUALLY_HIDDEN: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  overflow: "hidden",
  whiteSpace: "nowrap",
  clipPath: "inset(50%)",
};

/** One line, cut with an ellipsis. The other half of the clamping problem. */
export const CRC_TRUNCATE_STYLE: CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

interface CrcTextClampInput {
  readonly lines: CresultcardTitleLines;
}

/**
 * The one resolution from a line allowance to the style that enforces it.
 *
 * The three cases are not interchangeable and picking the wrong one is a
 * defect rather than a preference: `One` truncates, because
 * `-webkit-line-clamp: 1` puts its ellipsis at a line break that a `nowrap`
 * control never produces; `Full` only needs a break opportunity for an
 * unbroken string; everything between is a real clamp. Three primitives were
 * each deriving this ladder inline, which is three places for one of them to
 * pair a clamp with the `nowrap` that silently defeats it.
 */
export function crcTextClampStyle({ lines }: CrcTextClampInput): CSSProperties {
  if (lines === CresultcardTitleLines.One) {
    return { ...CRC_TRUNCATE_STYLE, display: "block" };
  }
  const clamp = CRC_TITLE_CLAMP[lines];
  return clamp == null ? { overflowWrap: "anywhere" } : crcClampLines(clamp);
}
